type Child = Node | string | number | null | undefined | false;
type Props = Record<string, unknown> & { class?: string; style?: Partial<CSSStyleDeclaration> | string };

/**
 * Crea elementos DOM. El texto siempre se inserta como nodo de texto (nunca innerHTML),
 * así el contenido de los jugadores no puede inyectar HTML.
 */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    for (const [k, v] of Object.entries(props)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === 'class') el.className = String(v);
      else if (k === 'style') {
        if (typeof v === 'string') el.setAttribute('style', v);
        else Object.assign(el.style, v);
      } else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k in el && typeof v !== 'string') (el as unknown as Record<string, unknown>)[k] = v;
      else el.setAttribute(k, v === true ? '' : String(v));
    }
  }
  append(el, children);
  return el;
}

export function append(el: Element, children: Child[]) {
  for (const c of children.flat() as Child[]) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

export function clear(el: Element) {
  while (el.firstChild) el.firstChild.remove();
}

/** Botón con sonido de clic. */
export function button(label: Child | Child[], onClick: () => void, cls = 'btn', extra: Props = {}) {
  return h('button', { class: cls, type: 'button', onClick: () => onClick(), ...extra }, ...(Array.isArray(label) ? label : [label]));
}

export function hex(n: number) {
  return `#${n.toString(16).padStart(6, '0')}`;
}

export function formatTime(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

let toastHost: HTMLElement | null = null;
export function toast(text: string, level: 'info' | 'success' | 'warn' | 'error' = 'info', ms = 3500) {
  if (!toastHost) {
    toastHost = h('div', { class: 'toasts', 'aria-live': 'polite' });
    document.body.append(toastHost);
  }
  const t = h('div', { class: `toast toast-${level}` }, text);
  toastHost.append(t);
  setTimeout(() => t.classList.add('out'), ms);
  setTimeout(() => t.remove(), ms + 400);
  while (toastHost.children.length > 5) toastHost.firstChild!.remove();
}

/** Diálogo modal sencillo. Devuelve una función para cerrarlo. */
export function modal(title: string, body: Child | Child[], actions: Array<{ label: string; cls?: string; run: () => void | boolean | Promise<void | boolean> }>, opts: { wide?: boolean } = {}) {
  const close = () => wrap.remove();
  const wrap = h(
    'div',
    { class: 'modal-backdrop', role: 'dialog', 'aria-modal': 'true' },
    h(
      'div',
      { class: `panel modal${opts.wide ? ' wide' : ''}` },
      h('h2', null, title),
      h('div', { class: 'modal-body' }, ...(Array.isArray(body) ? body : [body])),
      h(
        'div',
        { class: 'modal-actions' },
        ...actions.map((a) =>
          button(a.label, async () => {
            const keep = await a.run();
            if (keep !== true) close();
          }, a.cls ?? 'btn'),
        ),
      ),
    ),
  );
  document.body.append(wrap);
  return close;
}
